import React, { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
// Icônes conservées, leur couleur sera modifiée par CSS
import { X, CreditCard, Smartphone, Check, HeartHandshake } from 'lucide-react'; 
import { useToast } from '../../components/ui/use-toast';
import { formatCurrency } from '../../utils/formatters';
import { convertFCFAToEUR } from '../../utils/currencyConverter'; // La logique de conversion est conservée
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Contribution, CrowdfundingCampaign } from '../../types/crowdfunding';
import { contributionService, campaignService } from '../../services/crowdfunding';
import { initiateContributionPayment } from '../../services/crowdfunding/paymentService';
import { usePaymentStatus } from '../../hooks/usePaymentStatus';
import { useQuery } from '@tanstack/react-query';

// Les constantes et la logique métier sont conservées
const MIN_INTERNATIONAL_PAYMENT_AMOUNT = 1500;

// On met à jour la sémantique pour refléter le contexte de mécénat
const PAYMENT_METHODS = [
  { 
    id: 'mtn', 
    name: 'MTN & Orange Money', 
    logo: 'https://pgnkuxeckqnzkyfcrwzs.supabase.co/storage/v1/object/public/avatars//om-momo.jpg',
    icon: <Smartphone className="h-5 w-5" />,
    description: 'Paiement mobile (Cameroun)'
  },
  {
    id: 'paypal', 
    name: 'PayPal',
    logo: 'https://pgnkuxeckqnzkyfcrwzs.supabase.co/storage/v1/object/public/candidates/logos/images.png',
    icon: <CreditCard className="h-5 w-5" />,
    description: 'Paiement international sécurisé',
    minAmount: MIN_INTERNATIONAL_PAYMENT_AMOUNT
  },
  {
    id: 'stripe', 
    name: 'Cartes Bancaires',
    logo: 'https://pgnkuxeckqnzkyfcrwzs.supabase.co/storage/v1/object/public/candidates/logos/card.jpg',
    icon: <CreditCard className="h-5 w-5" />,
    description: 'Visa, Mastercard, etc.',
    minAmount: MIN_INTERNATIONAL_PAYMENT_AMOUNT
  },
];

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  contribution: Contribution;
  onPaymentSuccess: () => void;
}

export const PaymentModal: React.FC<PaymentModalProps> = ({
  isOpen,
  onClose,
  contribution,
  onPaymentSuccess
}) => {
  const [selectedPayment, setSelectedPayment] = useState<string | null>(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [paymentReference, setPaymentReference] = useState<string>();
  const { toast } = useToast();
  
  // Renommé "campaign" en "project" pour la clarté
  const { data: project } = useQuery({
    queryKey: ['project-for-donation', contribution.campaign_id],
    queryFn: async () => {
      if (!contribution.campaign_id) return null;
      return await campaignService.getCampaignById(contribution.campaign_id);
    },
    enabled: !!contribution.campaign_id
  });
  
  // La logique du hook est conservée
  usePaymentStatus({
    reference: paymentReference,
    paymentMethod: selectedPayment || '',
    onSuccess: () => {
      toast({ title: "Paiement réussi !", description: "Votre soutien a été enregistré. Merci infiniment." });
      contributionService.updatePaymentStatus(contribution.id, 'paid')
        .then(() => contributionService.updateCampaignAmount(contribution.campaign_id))
        .then(() => setTimeout(() => { onPaymentSuccess(); onClose(); }, 2000))
        .catch(error => {
          console.error('Erreur lors de la mise à jour du statut:', error);
          toast({ title: "Erreur de mise à jour", description: "Le paiement est validé mais une erreur est survenue lors de l'enregistrement.", variant: "destructive" });
        });
    },
    onError: (error) => {
      toast({ title: "Erreur de paiement", description: error.message, variant: "destructive" });
      setProcessing(false);
    },
  });

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhoneNumber(e.target.value);
    setPhoneError(null);
  };

  const handlePaymentMethodSelect = (method: string) => {
    setPhoneError(null);
    setSelectedPayment(method);
    if (method === 'mtn') setPhoneNumber('237');
    else setPhoneNumber(''); 
  };

  const handlePaymentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPayment) return;
    
    // La logique de validation est conservée
    if (selectedPayment === 'mtn' && !/^237\d{9}$/.test(phoneNumber)) {
      setPhoneError('Le numéro doit être au format 237xxxxxxxxx');
      return;
    }
    if (['paypal', 'stripe'].includes(selectedPayment) && contribution.amount < MIN_INTERNATIONAL_PAYMENT_AMOUNT) {
      toast({ title: "Montant insuffisant", description: `Le minimum pour ce moyen de paiement est de ${formatCurrency(MIN_INTERNATIONAL_PAYMENT_AMOUNT)} FCFA.`, variant: "destructive" });
      return;
    }
    
    try {
      setProcessing(true);
      const paymentOptions = { paymentMethod: selectedPayment, phoneNumber: selectedPayment === 'mtn' ? phoneNumber : undefined };
      const response = await initiateContributionPayment(contribution, paymentOptions);
      
      if (response.transaction.payment_reference) {
        setPaymentReference(response.transaction.payment_reference);
        if (response.transaction.payment_url) {
          window.location.href = response.transaction.payment_url;
        }
      }
    } catch (error) {
      console.error('Erreur lors du paiement:', error);
      toast({ title: "Erreur", description: error instanceof Error ? error.message : 'Une erreur est survenue.', variant: "destructive" });
      setProcessing(false);
    }
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/70 backdrop-blur-lg z-[100] animate-fade-in" />
        <Dialog.Content 
          className="fixed top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] w-[90vw] max-w-md max-h-[85vh] 
            rounded-xl bg-gradient-to-b from-gray-900 via-black to-black text-white 
            shadow-2xl shadow-red-900/20 focus:outline-none z-[101] 
            border border-white/10 animate-scale-in
            overflow-y-auto overscroll-contain p-6"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          <div className="flex items-center justify-between mb-4">
            <Dialog.Title className="text-xl font-bold text-white">Finaliser votre Don</Dialog.Title>
            <Dialog.Close asChild>
              <button className="rounded-full p-1 hover:bg-white/10 transition-colors" aria-label="Fermer"><X className="h-5 w-5" /></button>
            </Dialog.Close>
          </div>
          
          <div className="mb-6 p-4 bg-black/40 rounded-lg border border-white/10">
            <h3 className="text-lg font-semibold mb-3 text-red-400 flex items-center"><HeartHandshake className="w-5 h-5 mr-2"/>Détails de votre soutien</h3>
            <div className="flex items-center gap-4">
              {project?.image_url ? (
                <img src={project.image_url} alt={project.title} className="w-16 h-16 rounded-lg object-cover border-2 border-white/10"/>
              ) : (
                <div className="w-16 h-16 rounded-lg bg-black/30 flex items-center justify-center border-2 border-white/10">
                  <HeartHandshake className="w-8 h-8 text-red-500/50"/>
                </div>
              )}
              <div>
                <p className="font-medium text-white">Montant : <span className="text-red-400 font-bold">{formatCurrency(contribution.amount)} FCFA</span></p>
                <p className="text-gray-300">Projet : <span className="font-semibold">{project?.title}</span></p>
              </div>
            </div>
          </div>
          
          <form onSubmit={handlePaymentSubmit}>
            <p className="text-lg font-medium text-white/90 mb-4">Choisissez votre méthode de paiement</p>
            <div className="space-y-3">
              {PAYMENT_METHODS.map(method => (
                <div key={method.id}>
                  <button
                    type="button"
                    onClick={() => handlePaymentMethodSelect(method.id)}
                    disabled={method.minAmount && contribution.amount < method.minAmount}
                    className={`relative w-full flex items-center p-3 rounded-lg border-2 transition-all overflow-hidden
                      ${selectedPayment === method.id
                        ? 'border-red-500 bg-red-500/10 text-red-400 scale-105'
                        : method.minAmount && contribution.amount < method.minAmount
                          ? 'border-white/10 bg-black/30 opacity-50 cursor-not-allowed'
                          : 'border-white/10 bg-black/30 hover:bg-white/5 hover:border-white/20'
                      }`}
                  >
                    <div className="relative flex items-center w-full">
                      <div className="w-12 h-12 mr-3 bg-white/5 rounded-md flex items-center justify-center">
                        <img src={method.logo} alt={method.name} className="h-8 object-contain"/>
                      </div>
                      <div>
                        <span className="font-semibold text-white text-left">{method.name}</span>
                        <p className="text-xs text-gray-400 text-left">{method.description}</p>
                        {method.minAmount && contribution.amount < method.minAmount && (
                          <p className="text-xs text-red-500 mt-1 text-left">Minimum {formatCurrency(method.minAmount)} FCFA</p>
                        )}
                      </div>
                      {selectedPayment === method.id && <Check className="w-5 h-5 text-red-500 ml-auto" />}
                    </div>
                  </button>
                  
                  {selectedPayment === method.id && (
                    <div className="mt-3 p-4 bg-red-500/5 rounded-b-lg">
                      {selectedPayment === 'mtn' && (
                        <div>
                          <label htmlFor="phone" className="block text-sm font-medium text-white/80 mb-1">Numéro de téléphone</label>
                          <Input id="phone" type="tel" value={phoneNumber} onChange={handlePhoneNumberChange} placeholder="237xxxxxxxxx" className="bg-black/20 border-white/20 text-white placeholder-gray-500 focus:ring-red-500"/>
                          {phoneError && <p className="text-red-400 text-xs mt-1">{phoneError}</p>}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-6">
              <Button
                type="submit"
                className="w-full text-lg font-bold py-6 bg-red-600 hover:bg-red-700 text-white shadow-lg shadow-red-900/30 disabled:opacity-60 disabled:cursor-not-allowed"
                disabled={!selectedPayment || processing || (selectedPayment === 'mtn' && !phoneNumber)}
              >
                {processing ? (
                  <>
                    <span className="inline-block w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Traitement...
                  </>
                ) : (
                  `Payer ${['paypal', 'stripe'].includes(selectedPayment || '') 
                    ? `€${convertFCFAToEUR(contribution.amount).toFixed(2)}` 
                    : `${formatCurrency(contribution.amount)} FCFA`}`
                )}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};